import { forwardRef, type ReactNode } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cn } from '@/lib/utils'

/**
 * NavItem — пункт боковой навигации (`.nav-item` макета). Состояния default/hover/focus/active.
 * Активный помечается aria-current="page"; в списке активен ровно один. Доступен с клавиатуры.
 */
export interface NavItemProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  active?: boolean
  icon?: ReactNode
  /**
   * Значок справа от подписи (напр. индикатор заказов, требующих правок, у «Мои заказы» —
   * centrika-docs!98). Подпись усекается, значок не сжимается; подсветка активного не меняется.
   */
  trailing?: ReactNode
  /**
   * Длинная подпись переносится на следующую строку, а не усекается (подпункты групп сайдбара:
   * «Проектные работы», «Абонентское обслуживание» — ecology-sites, как в макете раздела).
   */
  wrap?: boolean
  asChild?: boolean
}

export const NavItem = forwardRef<HTMLAnchorElement, NavItemProps>(
  ({ className, active, icon, trailing, wrap, asChild, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'a'
    return (
      <Comp
        ref={ref}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'mb-0.5 flex items-center gap-3 rounded-9 px-3 py-2.5 font-medium outline-none transition-colors',
          'focus-visible:ring-2 focus-visible:ring-accent',
          active
            ? 'bg-accent-bg text-accent-txt'
            : 'text-txt-secondary hover:bg-hover hover:text-txt',
          className,
        )}
        {...props}
      >
        {icon}
        <span className={wrap ? 'min-w-0 break-words' : 'truncate'}>{children}</span>
        {trailing ? <span className="ml-auto flex shrink-0 items-center">{trailing}</span> : null}
      </Comp>
    )
  },
)
NavItem.displayName = 'NavItem'
